import React, { useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Alert } from 'react-bootstrap';
import axios from 'axios';
import API_BASE_URL from '../config';
import './UpgradeModal.css';

// Check if Stripe publishable key is configured
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// Validate that it's a publishable key (starts with pk_), not a secret key (sk_)
if (STRIPE_PUBLISHABLE_KEY) {
  if (STRIPE_PUBLISHABLE_KEY.startsWith('sk_')) {
    console.error('ERROR: REACT_APP_STRIPE_PUBLISHABLE_KEY is set to a SECRET key. Please use a PUBLISHABLE key (starts with pk_).');
  } else if (!STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) {
    console.error('ERROR: REACT_APP_STRIPE_PUBLISHABLE_KEY does not look like a valid publishable key. It should start with pk_.');
  }
}

if (!STRIPE_PUBLISHABLE_KEY || !STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) {
  console.warn('REACT_APP_STRIPE_PUBLISHABLE_KEY is not set correctly. Stripe payment will not work.');
  console.warn('Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file to a valid publishable key (starts with pk_).');
}

// Only load Stripe if we have a valid publishable key
const stripePromise = (STRIPE_PUBLISHABLE_KEY && STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) 
  ? loadStripe(STRIPE_PUBLISHABLE_KEY) 
  : null;

const UpgradeModal = ({ isOpen, onClose, userPlan }) => {
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [price, setPrice] = useState(null);

  React.useEffect(() => {
    if (isOpen) {
      fetchPrice();
      if (!clientSecret) {
        createPaymentIntent();
      }
    }
  }, [isOpen]);

  const fetchPrice = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${API_BASE_URL}/api/payment/price`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data) {
        setPrice(response.data);
      }
    } catch (error) {
      console.error('Error fetching price:', error);
      // Set default price if fetch fails
      setPrice({ amountInDollars: '10.00', currency: 'usd' });
    }
  };

  const createPaymentIntent = async () => {
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (!token) {
        setError('Please login to continue with payment.');
        return;
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/payment/create-intent`,
        { plan: 'standard' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data && response.data.clientSecret) {
        setClientSecret(response.data.clientSecret);
      } else {
        setError('Invalid response from server. Please try again.');
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Failed to initialize payment. Please check your Stripe configuration.';
      setError(errorMessage);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="upgrade-modal-overlay" onClick={onClose}>
      <div className="upgrade-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="upgrade-modal-close" onClick={onClose}>×</button>
        <h2>Upgrade to Standard Plan</h2>
        {price && (
          <div className="upgrade-modal-price">
            <span className="price-amount">${price.amountInDollars}</span>
            <span className="price-period">/ month</span>
          </div>
        )}
        <p className="upgrade-modal-description">
          Get 7 million tokens for 1 month. Perfect for teams that need more capacity.
        </p>
        <div className="upgrade-features">
          <div className="upgrade-feature">
            <span className="feature-icon">✓</span>
            <span>7 million tokens</span>
          </div>
          <div className="upgrade-feature">
            <span className="feature-icon">✓</span>
            <span>1 month validity</span>
          </div>
          <div className="upgrade-feature">
            <span className="feature-icon">✓</span>
            <span>Full access to all features</span>
          </div>
          <div className="upgrade-feature">
            <span className="feature-icon">✓</span>
            <span>Priority support</span>
          </div>
        </div>
        {error && <div className="upgrade-error">{error}</div>}
        {(!STRIPE_PUBLISHABLE_KEY || !STRIPE_PUBLISHABLE_KEY.startsWith('pk_')) && (
          <div className="upgrade-error">
            Stripe payment is not configured correctly. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY in your .env file.
            <br />
            <strong>Important:</strong> Use a PUBLISHABLE key (starts with pk_), NOT a secret key (sk_).
            <br />
            Get your publishable key from: <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer">Stripe Dashboard</a>
          </div>
        )}
        {STRIPE_PUBLISHABLE_KEY && clientSecret && stripePromise && (
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <CheckoutForm onSuccess={onClose} setError={setError} />
          </Elements>
        )}
        {STRIPE_PUBLISHABLE_KEY && !clientSecret && !error && (
          <div className="upgrade-loading">Initializing payment...</div>
        )}
      </div>
    </div>
  );
};

const CheckoutForm = ({ onSuccess, setError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/profile?tab=billing`,
        },
        redirect: 'if_required',
      });

      if (error) {
        setError(error.message);
        setLoading(false);
      } else if (paymentIntent && paymentIntent.status === 'succeeded') {
        // Notify backend of successful payment
        try {
          await axios.post(
            `${API_BASE_URL}/api/payment/confirm`,
            { paymentIntentId: paymentIntent.id },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setSuccessMessage('Payment successful! You now have 7 million tokens for 1 month.');
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } catch (err) {
          console.error('Error confirming payment:', err);
          setError('Payment succeeded but failed to update account. Please contact support.');
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('An error occurred during payment. Please try again.');
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {successMessage && (
        <Alert variant="success" className="mb-3">
          {successMessage}
        </Alert>
      )}
      <PaymentElement />
      <button
        type="submit"
        disabled={!stripe || loading}
        className="upgrade-pay-button"
      >
        {loading ? 'Processing...' : 'Pay Now'}
      </button>
    </form>
  );
};

export default UpgradeModal;

